import { TimelineLite } from "gsap";
import * as HLF from "html-living-framework";
import { Application } from "Singletons/Application";
import { StoryLoadScreen } from "States/StoryLoadScreen";
import * as Constants from "../Data/Constants";
import { Button } from "../GameObjects/Button";

export class ChapterMenu extends HLF.State {
	protected _back: string[] = [];
	protected backButton?: Button;
    private readonly _bg: PIXI.Sprite;
    private readonly _buttons: Button[];

    constructor(pData: {
        chapter: number,
        pages: string[],
        next?: string,
    }) {
        super();

        this._bg = new PIXI.Sprite(PIXI.Texture.WHITE);
        this._bg.anchor.set(0.5);
        this._bg.interactive = true;
        this.addChild(this._bg);

        this._buttons = new Array<Button>();

        this.makeStoryButton(`Chapter ${pData.chapter}\nTitle Screen`, "ch" + pData.chapter + "_title", 0, -300);

        let x = -450;
        let y = -150;

        for (let i = 0; i<pData.pages.length; i++) {
            const sceneToLoad = "ch" + pData.chapter + "_p" + pData.pages[i];
            const title = `Chapter ${pData.chapter}\nPage ${pData.pages[i]}`;
            this.makeStoryButton(title, sceneToLoad, x, y);
            x += 150;
            if (x>450) {
                y += 150;
                x = -450;
            }
        }

        if (pData.next) {
            this.makeStoryButton(`Next Chapter`, pData.next, x, y);
        }
    }

    /**
     * Currently acts like a delayed contructor.
     * @param pSize The current window size.
     */
    public init(pSize: PIXI.Point, pData?: {name: string, back?: string[]}) {
		if (pData) {
			this.name = pData.name; // helps with debugging, also using it for back button functionality
			this._back = pData.back ?? [];
			if (this._back.length) {
				this.backButton = new Button(this.gotoPreviousState.bind(this), "button-default", ["ui-1", "ui-2"]);
				this.backButton.addText("Back", Constants.FONT_GEL_MED_35, 96);
				this.backButton.alpha = 0.25;
                this.addChild(this.backButton);
                this._buttons.push(this.backButton);
			}
		}
		// super.init calls onResize
		super.init(pSize, pData);

        PubSub.publishSync(HLF.REGISTER_FOCUSABLES, this._buttons);

        this.onResize(pSize);
    }

    /**
     * Called when a state is destroyed.
     */
    public destroy(pOptions?: {children?: boolean; texture?: boolean; baseTexture?: boolean;}): void {
        super.destroy(pOptions);

        PubSub.publishSync(HLF.CLEAR_FOCUS, undefined);
        PubSub.publishSync(HLF.REGISTER_FOCUSABLES, this._buttons);
    }

    public onResize(pSize: PIXI.Point) {
		super.onResize(pSize);
		const {x:width, y:height} = pSize;
		if (this.backButton) {
			// bottom right
			this.backButton.position.set(
				(width/2) - (this.backButton.width / 2),
				(height/2) - (this.backButton.height / 2),
			);
        }
        this._bg.width = width;
        this._bg.height = height;
	}

    /**
     * Called after load screen has animated out.
     * If no animation is required, simply remove this function.
     * @param pOnComplete Callback when completed.
     */
    public animateIn(pOnComplete: () => void): void {
        const timeline: TimelineLite = new TimelineLite();
        for (let i = 0; i < this._buttons.length; i++) {
            const element = this._buttons[i];
            timeline.to(element.scale, 0.5, {
                x: 1,
                y: 1,
            }, 0.1 * i);
        }
        timeline.call(this.onAnimateInComplete.bind(this));
        timeline.eventCallback(Constants.TWEEN_ONCOMPLETE, pOnComplete);
    }

    /**
     * Called when transition to leave starts.
     * If no animation is required, simply remove this function.
     * @param pOnComplete Callback when completed.
     */
    public animateOut(pOnComplete: () => void): void {
        const timeline: TimelineLite = new TimelineLite();
        for (let i = 0; i < this._buttons.length; i++) {
            const element = this._buttons[i];
            element.interactive = false;
            timeline.to(element.scale, 0.5, {
                x: 0,
                y: 0,
            }, 0.1 * i);
        }
        timeline.eventCallback(Constants.TWEEN_ONCOMPLETE, pOnComplete);
    }

    /**
     * Called once per frame.
     * @param pDeltaTime Time it took to render the previous frame.
     */
    public update(pDeltaTime: number) {
        super.update(pDeltaTime);
    }

    public gotoPreviousState() {
		this.interactiveChildren = false;
		this.interactive = false;
		this.buttonMode = false;
		this.accessible = false;
		const stateToLoad = this._back.pop()!;
		const steps = [
            HLF.TransitionStep.ShowLoadScreen,
            HLF.TransitionStep.RemoveCurrent,
			HLF.TransitionStep.UnloadAssets,
            HLF.TransitionStep.LoadAssets,
            HLF.TransitionStep.AttachNewBehind,
            HLF.TransitionStep.PauseAFrame,
            HLF.TransitionStep.AnimNewIn,
            HLF.TransitionStep.HideLoadScreen,
        ];
        PubSub.publishSync(HLF.STATE_LOAD_STATE, new HLF.StateToken(
			{id: stateToLoad, data: {
				name: stateToLoad,
				back: this._back, // don't add this state to the history
			}}, StoryLoadScreen.NAME, ...steps)
		);
	}

    private makeStoryButton(title: string, sceneToLoad: string, x:number, y:number) {
        const steps = [
			HLF.TransitionStep.AnimCurrentOut,
            HLF.TransitionStep.RemoveCurrent,
            HLF.TransitionStep.ShowLoadScreen,
            HLF.TransitionStep.LoadAssets,
            HLF.TransitionStep.AttachNewBehind,
            HLF.TransitionStep.PauseAFrame,
            HLF.TransitionStep.AnimNewIn,
            HLF.TransitionStep.HideLoadScreen,
            HLF.TransitionStep.UnloadAssets,
        ];

        const onButtonPressed = ()=> {
            PubSub.publishSync( HLF.STATE_LOAD_STATE, new HLF.StateToken(
                { id: sceneToLoad, data: {
                    name: sceneToLoad,
                    back: [...this._back, this.name],
                 }}, Constants.LOAD_INTERSTITIAL, ...steps ));
        }
        const newButton = new Button(onButtonPressed, "button-default", Constants.SPRITESHEET_UI);
        newButton.scale.set(0);
        newButton.interactive = false;
        newButton.position.set(x, y);
        newButton.addText(title, Constants.FONT_GEL_MED_35, 35);
        this.addChild(newButton);
        this._buttons.push(newButton);
        return newButton;
    }

    private onAnimateInComplete(): void {
        for (let i = 0; i < this._buttons.length; i++) {
            const element = this._buttons[i];
            element.interactive = true;
        }
    }
}
